import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, User, Clock, Sparkles } from 'lucide-react';
import { PostCard } from '@/components/PostCard';
import { CategoryTags } from '@/components/CategoryTags';
import { CreatePostModal } from '@/components/CreatePostModal';
import { BottomNav } from '@/components/BottomNav';
import { Avatar } from '@/components/Avatar';
import { FollowingSidebar } from '@/components/FollowingSidebar';
import { CommunitiesSidebar } from '@/components/CommunitiesSidebar';

const TIME_OPTIONS = ['All', '5 mins', '15 mins', '30 mins', '1 hr', '1-2 hrs', '2+ hrs'];

const FeedPage = () => {
  const { user, logout } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTime, setSelectedTime] = useState('All');
  
  // Load sort preference from localStorage
  const [sortBy, setSortBy] = useState(() => {
    const savedSort = localStorage.getItem('feedSortPreference');
    return savedSort || 'recent';
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle URL parameters for category filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory('All');
    }
  }, [location.search]);

  // Save sort preference when changed
  useEffect(() => {
    localStorage.setItem('feedSortPreference', sortBy);
  }, [sortBy]);

  useEffect(() => {
    fetchIdeas();
  }, [selectedCategory, selectedTime, sortBy]);

  const fetchIdeas = async () => {
    try {
      const params = { sort_by: sortBy };
      if (selectedCategory !== 'All') params.category = selectedCategory;
      if (selectedTime !== 'All') params.time_needed = selectedTime;

      const response = await axios.get('/ideas', { params });
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to fetch ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      const response = await axios.post(`/ideas/${ideaId}/vote`, { vote_type: voteType });
      setIdeas(ideas.map(idea => idea.id === ideaId ? response.data : idea));
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleCreatePost = async (formData) => {
    if (!formData.title || !formData.description || !formData.category || !formData.time_needed) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post('/ideas', formData);
      toast.success('Idea posted!');
      setIsModalOpen(false);
      fetchIdeas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-0">
      {/* NavBar */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-40 bg-white shadow-soft border-b-2 border-grayLight"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_0639c081-d9f0-47e7-918b-f72657c5b82c/artifacts/6zzokka9_boredom%20buster%20logo.svg" 
              alt="BoredomBuster" 
              className="h-8 sm:h-10 w-auto"
            />
          </motion.div>

          <div className="flex items-center gap-2 sm:gap-3">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                data-testid="submit-idea-btn"
                onClick={() => setIsModalOpen(true)}
                className="hidden md:flex bg-primary hover:bg-primary/90 text-primaryDark border-2 border-primaryDark rounded-md font-bold px-4 sm:px-6 shadow-soft focus-ring text-sm sm:text-base h-9 sm:h-10"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Create Post
              </Button>
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/my-ideas')}
              data-testid="my-ideas-btn"
              className="hidden md:flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-grayLight transition-colors focus-ring text-sm sm:text-base"
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-bold hidden lg:inline">My Ideas</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05, rotate: 15 }}
              whileTap={{ scale: 0.95 }}
              onClick={logout}
              data-testid="logout-btn"
              className="p-1.5 sm:p-2 rounded-md hover:bg-accentRed/10 hover:text-accentRed transition-colors focus-ring"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto flex gap-6">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-60 sticky top-20 h-fit space-y-4">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="bg-white rounded-lg shadow-sm p-5 space-y-4 border border-cardBorder"
          >
            <div>
              <h3 className="font-baloo font-bold text-lg text-primaryDark mb-2">About</h3>
              <p className="text-sm text-gray leading-relaxed">
                Share creative ideas to beat boredom. Vote on activities you love!
              </p>
            </div>
            <div className="pt-4 border-t border-cardBorder">
              <div className="flex items-center gap-3">
                <Avatar username={user?.username} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-primaryDark truncate">@{user?.username}</div>
                  <div className="text-xs text-grayMeta truncate">{user?.email}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Following Section */}
          <FollowingSidebar />

          {/* Communities Section */}
          <CommunitiesSidebar />
        </aside>

        {/* Main Feed */}
        <main className="flex-1 max-w-[680px] mx-auto px-3 sm:px-4 py-4 sm:py-6 w-full">
          {/* Filters */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6 space-y-3 sm:space-y-4 border border-cardBorder"
          >
            <CategoryTags selected={selectedCategory} onSelect={setSelectedCategory} />

            <div className="flex gap-2 sm:gap-3">
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger data-testid="time-filter" className="flex-1 h-10 sm:h-11 rounded-lg border border-cardBorder hover:bg-surface focus-ring text-xs sm:text-sm font-semibold text-grayMeta">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <SelectValue placeholder="Time" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-cardBorder">
                  {TIME_OPTIONS.map((time) => (
                    <SelectItem key={time} value={time} className="text-xs sm:text-sm font-semibold">{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-filter" className="flex-1 h-10 sm:h-11 rounded-lg border border-cardBorder hover:bg-surface focus-ring text-xs sm:text-sm font-semibold text-grayMeta">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-cardBorder">
                  <SelectItem value="popular" className="text-xs sm:text-sm font-semibold">Most Popular</SelectItem>
                  <SelectItem value="recent" className="text-xs sm:text-sm font-semibold">Most Recent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          {/* Ideas Feed */}
          {loading ? (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg mx-auto"
              />
              <p className="text-gray mt-4 text-sm sm:text-base font-bold">Loading ideas...</p>
            </div>
          ) : ideas.length === 0 ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-lg shadow-soft p-8 sm:p-12 text-center border border-grayLight"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary to-accentCyan rounded-lg mx-auto mb-4 flex items-center justify-center shadow-soft">
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-primaryDark" />
              </div>
              <h3 className="font-baloo font-bold text-lg sm:text-xl text-grayDark mb-2">No ideas yet!</h3>
              <p className="text-gray mb-6 text-sm sm:text-base">Be the first to share something amazing</p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-primaryDark border-2 border-primaryDark rounded-md px-6 shadow-soft font-bold text-sm sm:text-base"
                >
                  Create First Post
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            ideas.map((idea, index) => (
              <PostCard key={idea.id} idea={idea} index={index} onVote={handleVote} currentUser={user} />
            ))
          )}
        </main>
      </div>

      {/* Bottom Nav */}
      <BottomNav onCreateClick={() => setIsModalOpen(true)} />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default FeedPage;
